import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { TestTube } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const CAMPAIGNS = [
  { key: "delivery_confirmation", label: "Email Consegna (Google Review)" },
  { key: "save_cart", label: "Save Cart (Carrelli abbandonati)" },
];

/**
 * Admin widget — CTR per variant across multiple A/B campaigns.
 * Campaign selector lets admin compare delivery_confirmation vs save_cart.
 */
export const EmailABStatsWidget = () => {
  const [campaign, setCampaign] = useState("delivery_confirmation");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      setLoading(true);
      try {
        const token = sessionStorage.getItem("access_token");
        const res = await axios.get(`${API}/dashboard/email-ab-stats?campaign=${campaign}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setStats(res.data);
      } catch (err) {
        console.warn("[ab-email] stats fetch failed:", err?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [campaign]);

  const sorted = [...(stats?.variants || [])].sort((a, b) => b.ctr_percent - a.ctr_percent);
  const leader = sorted[0]?.ctr_percent > 0 ? sorted[0].variant : null;

  return (
    <Card data-testid="email-ab-stats">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <TestTube className="w-5 h-5 text-indigo-500" />
          Email A/B Test — CTR
          <div className="ml-auto flex gap-2">
            {CAMPAIGNS.map((c) => (
              <button
                key={c.key}
                onClick={() => setCampaign(c.key)}
                className={`text-xs px-3 py-1 rounded-full transition ${
                  campaign === c.key
                    ? "bg-indigo-100 text-indigo-700 font-semibold"
                    : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                }`}
                data-testid={`ab-campaign-${c.key}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-gray-400 text-sm text-center py-4">Caricamento…</p>
        ) : !stats || stats.total_sent === 0 ? (
          <p className="text-gray-500 text-sm text-center py-4" data-testid="ab-stats-empty">
            Nessuna email inviata ancora per <strong>{campaign}</strong>.
            {campaign === "save_cart" &&
              " I dati appariranno dopo il primo cron POST /api/cart/send-abandoned-reminders."}
            {campaign === "delivery_confirmation" &&
              " I dati appariranno dopo i primi ordini marcati come 'delivered'."}
          </p>
        ) : (
          <div className="space-y-3">
            {stats.variants.map((v) => {
              const isLeader = leader === v.variant;
              const maxCtr = Math.max(...stats.variants.map((x) => x.ctr_percent), 1);
              const pct = (v.ctr_percent / maxCtr) * 100;
              return (
                <div key={v.variant} data-testid={`ab-stats-${v.variant}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700 font-medium">
                      Variante {v.variant.toUpperCase()}
                      {isLeader && (
                        <Badge className="ml-2 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          🏆 Leader
                        </Badge>
                      )}
                    </span>
                    <span className="text-gray-900 font-semibold">
                      {v.ctr_percent}%{" "}
                      <span className="text-gray-400 font-normal">
                        ({v.clicked}/{v.sent})
                      </span>
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-500 ${
                        isLeader
                          ? "bg-gradient-to-r from-emerald-500 to-emerald-600"
                          : "bg-gradient-to-r from-indigo-400 to-indigo-500"
                      }`}
                      style={{ width: `${Math.max(pct, 3)}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
            <div className="pt-3 border-t border-gray-200 text-xs text-gray-500">
              Totale inviate: <strong className="text-gray-700">{stats.total_sent}</strong>
              {" · "}Click totali: <strong className="text-gray-700">{stats.total_clicked}</strong>
              {" · "}
              <span className="italic">Distribuzione 50/50 deterministica via hash.</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
